/**
 * Painel de Extração de Entidades
 * Exibe pessoas, lugares, datas, evidências e termos legais identificados
 */

import React, { useState, useEffect } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonIcon from '@mui/icons-material/Person'
import BusinessIcon from '@mui/icons-material/Business'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import GavelIcon from '@mui/icons-material/Gavel'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import VerifiedIcon from '@mui/icons-material/Verified'
import LinkIcon from '@mui/icons-material/Link'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { EntityExtraction, Entity, EntityType } from '../../types/transcription'
import { getEntityExtraction } from '../../services/api'
import { contextualErrors, FriendlyError } from '../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'

type Props = {
  fileId: string
  onNavigateToSegment?: (segmentIndex: number) => void
}

const ENTITY_CONFIG: Record<EntityType, { label: string; icon: React.ElementType; color: string; bgColor: string }> = {
  PERSON: {
    label: 'Pessoas',
    icon: PersonIcon,
    color: '#1976d2',
    bgColor: '#e3f2fd'
  },
  ORGANIZATION: {
    label: 'Organizações',
    icon: BusinessIcon,
    color: '#7b1fa2',
    bgColor: '#f3e5f5'
  },
  LOCATION: {
    label: 'Locais',
    icon: LocationOnIcon,
    color: '#388e3c',
    bgColor: '#e8f5e9'
  },
  DATE: {
    label: 'Datas',
    icon: CalendarTodayIcon,
    color: '#f57c00',
    bgColor: '#fff3e0'
  },
  EVIDENCE: {
    label: 'Evidências',
    icon: VerifiedIcon,
    color: '#d32f2f',
    bgColor: '#ffebee'
  },
  LEGAL_TERM: {
    label: 'Termos Legais',
    icon: GavelIcon,
    color: '#455a64',
    bgColor: '#eceff1'
  },
  MONEY: {
    label: 'Valores',
    icon: AttachMoneyIcon,
    color: '#2e7d32',
    bgColor: '#e8f5e9'
  }
}

/**
 * Formata o tempo para exibição.
 * Aceita tanto segundos (número ou string numérica) quanto formato HH:MM:SS.
 */
function formatTime(time: string): string {
  if (!time) return '0:00'
  
  // Se já está no formato HH:MM:SS ou MM:SS, retorna diretamente (ou simplificado)
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      // HH:MM:SS - remover horas se for 00
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    // MM:SS - retorna como está
    return time
  }
  
  // Se for número de segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Configuração padrão para tipos de entidade desconhecidos
const DEFAULT_ENTITY_CONFIG = {
  label: 'Outros',
  icon: LinkIcon,
  color: '#757575',
  bgColor: '#f5f5f5'
}

/**
 * Converte o formato do backend para o formato esperado pelo frontend.
 * Backend retorna: segments (number[]), context (string), startTime (string)
 * Frontend espera: occurrences (array com segmentIndex, startTime, context)
 */
function buildOccurrencesFromBackend(entity: any): { segmentIndex: number; startTime: string; context: string }[] {
  const segments = entity.segments || []
  const context = entity.context || entity.value || ''
  const startTime = entity.startTime || '0:00'
  
  // Se não há segmentos, criar uma ocorrência padrão
  if (segments.length === 0) {
    return [{ segmentIndex: 0, startTime, context }]
  }
  
  // Criar uma ocorrência para cada segmento
  return segments.map((segmentIndex: number) => ({
    segmentIndex,
    startTime,
    context
  }))
}

function EntityCard({ entity, onNavigateToSegment }: { entity: Entity; onNavigateToSegment?: (segmentIndex: number) => void }) {
  const config = ENTITY_CONFIG[entity.type] || DEFAULT_ENTITY_CONFIG
  const Icon = config.icon
  const [expanded, setExpanded] = useState(false)

  // Compatibilidade: backend retorna confidence, frontend espera relevance
  const relevance = (entity as any).confidence ?? entity.relevance ?? 0.5
  
  // Compatibilidade: backend retorna mentions, frontend espera count
  const count = (entity as any).mentions ?? entity.count ?? 1
  
  // Compatibilidade: converter formato do backend para occurrences
  const occurrences = entity.occurrences ?? buildOccurrencesFromBackend(entity)

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e0e0e0',
        borderLeft: `4px solid ${config.color}`,
        mb: 1.5,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 1,
          borderColor: config.color
        }
      }}
    >
      <Box
        sx={{
          p: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: config.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon sx={{ color: config.color }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {entity.value}
          </Typography>
          {entity.normalizedValue && entity.normalizedValue !== entity.value && (
            <Typography variant="caption" sx={{ color: '#666' }}>
              → {entity.normalizedValue}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Ocorrências">
            <Chip
              label={count}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
          <Tooltip title="Relevância">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {[...Array(5)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 6,
                    height: 16,
                    bgcolor: i < Math.round(relevance * 5) ? config.color : '#e0e0e0',
                    mx: 0.15,
                    borderRadius: 0.5
                  }}
                />
              ))}
            </Box>
          </Tooltip>
          <ExpandMoreIcon
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              color: '#666'
            }}
          />
        </Box>
      </Box>

      {expanded && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', display: 'block', mb: 1 }}>
            Contexto das ocorrências:
          </Typography>
          {occurrences.map((occ, i) => (
            <Box
              key={i}
              sx={{
                p: 1.5,
                bgcolor: '#f9f9f9',
                borderRadius: 1,
                mb: 1,
                borderLeft: `3px solid ${config.color}`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={formatTime(occ.startTime)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem' }}
                  />
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Segmento #{occ.segmentIndex + 1}
                  </Typography>
                </Box>
                {onNavigateToSegment && (
                  <Tooltip title="Ir para o segmento na transcrição">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        onNavigateToSegment(occ.segmentIndex)
                      }}
                      sx={{ 
                        color: config.color,
                        '&:hover': { bgcolor: config.bgColor }
                      }}
                    >
                      <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555' }}>
                "{occ.context}"
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  )
}

function RelationshipCard({ relationship }: { 
  relationship: { entity1: string; entity2: string; relationship: string; context: string } 
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid #e0e0e0',
        mb: 1.5,
        bgcolor: '#fafafa'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Chip label={relationship.entity1} size="small" color="primary" variant="outlined" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 20, height: 2, bgcolor: '#1976d2' }} />
          <LinkIcon sx={{ color: '#1976d2', fontSize: 18 }} />
          <Box sx={{ width: 20, height: 2, bgcolor: '#1976d2' }} />
        </Box>
        <Chip label={relationship.entity2} size="small" color="primary" variant="outlined" />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
        {relationship.relationship}
      </Typography>
      <Typography variant="caption" sx={{ color: '#666' }}>
        {relationship.context}
      </Typography>
    </Paper>
  )
}

export default function EntityExtractionPanel({ fileId, onNavigateToSegment }: Props) {
  const [extraction, setExtraction] = useState<EntityExtraction | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [activeType, setActiveType] = useState<EntityType | 'all' | 'relationships'>('all')

  const loadExtraction = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getEntityExtraction(fileId)
      setExtraction(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar entidades:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadExtraction()
  }, [fileId])

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2, color: '#666' }}>
            Extraindo entidades...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ fontSize: 48, mb: 2 }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button variant="outlined" onClick={loadExtraction} startIcon={<RefreshIcon />}>
              Tentar novamente
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!extraction) {
    return (
      <AnalysisNotAvailableCard 
        title="Extração de Entidades" 
        onRetry={loadExtraction} 
      />
    )
  }

  // Variáveis seguras com fallbacks para evitar erros de undefined
  const entities = extraction.entities ?? []
  const relationships = extraction.relationships ?? []
  const byType = extraction.byType ?? {} as Record<EntityType, Entity[]>

  const entityTypes = Object.keys(ENTITY_CONFIG) as EntityType[]
  const filteredEntities = activeType === 'all' || activeType === 'relationships'
    ? entities
    : byType[activeType] || []

  return (
    <Box>
      {/* Resumo */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              🔍 Extração de Entidades
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadExtraction}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Atualizar
            </Button>
          </Box>

          {/* Cards de contagem por tipo */}
          <Grid container spacing={2}>
            {entityTypes.map((type) => {
              const config = ENTITY_CONFIG[type]
              const Icon = config.icon
              const count = byType[type]?.length || 0
              
              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={type}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      border: '1px solid #e0e0e0',
                      bgcolor: activeType === type ? config.bgColor : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: config.bgColor,
                        borderColor: config.color
                      }
                    }}
                    onClick={() => setActiveType(type)}
                  >
                    <Badge badgeContent={count} color="primary">
                      <Icon sx={{ fontSize: 32, color: config.color }} />
                    </Badge>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
                      {config.label}
                    </Typography>
                  </Paper>
                </Grid>
              )
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs de Filtro */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeType}
          onChange={(_, v) => setActiveType(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="all" label={`Todas (${entities.length})`} />
          {entityTypes.map((type) => {
            const count = byType[type]?.length || 0
            if (count === 0) return null
            return (
              <Tab
                key={type}
                value={type}
                label={`${ENTITY_CONFIG[type].label} (${count})`}
              />
            )
          })}
          <Tab value="relationships" label={`Relacionamentos (${relationships.length})`} />
        </Tabs>
      </Box>

      {/* Lista de Entidades ou Relacionamentos */}
      {activeType === 'relationships' ? (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Relacionamentos Detectados
            </Typography>
            
            {relationships.map((rel, index) => (
              <RelationshipCard key={index} relationship={rel} />
            ))}

            {relationships.length === 0 && (
              <Typography sx={{ textAlign: 'center', color: '#666', py: 4 }}>
                Nenhum relacionamento detectado
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              {activeType === 'all' 
                ? `Todas as Entidades (${entities.length})`
                : `${ENTITY_CONFIG[activeType].label} (${filteredEntities.length})`
              }
            </Typography>

            {filteredEntities.map((entity, index) => (
              <EntityCard key={entity.id || index} entity={entity} onNavigateToSegment={onNavigateToSegment} />
            ))}

            {filteredEntities.length === 0 && (
              <Typography sx={{ textAlign: 'center', color: '#666', py: 4 }}>
                Nenhuma entidade encontrada nesta categoria
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Legenda */}
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          💡 <strong>Dica:</strong> Clique em uma entidade para ver o contexto completo onde ela aparece.
          As barras de relevância indicam a importância da entidade no documento (quanto mais barras preenchidas, mais relevante).
          {onNavigateToSegment && ' Use o botão ➔ para ir diretamente ao segmento na aba Transcrição.'}
        </Typography>
      </Box>
    </Box>
  )
}
